/**
 * help-menu.js — the Help button, in row 2's right anchor.
 *
 * The third of the ⋮ menu's four concepts to get a real home (comments went to
 * Share in CMD-4, history to the save pill in CMD-4, code injection and links to
 * the left rail and the page pane). What is left of the junk drawer after this
 * is nothing, which is what lets CMD-9 delete it rather than re-label it.
 *
 * ── Why ⌘K is the first row ───────────────────────────────────────────────
 * `CMDK_HINT_HUNT_THRESHOLD` in the old bar re-pulsed a hint chip when it caught
 * a user opening three dropdowns without finding anything. That heuristic was
 * well built and diagnosed a real defect: the palette had no labelled home, so a
 * user hunting through menus never met it. It has one here, and a second in the
 * page pane — which is what makes the hint (and the heuristic) deletable.
 */

import { HELP_LABELS } from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { openPopoverMenu, MENU_EXIT_MS } from '../popover-menu.js';

export class HelpMenu {
  /** @param {HTMLElement} container — the `row2.view` slot */
  constructor(container) {
    this._container = container;
    this._render();
  }

  get button() {
    return this._btn;
  }

  destroy() {
    this._btn = null;
  }

  _render() {
    this._btn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-cb-help',
      id: 'pb-help-btn',
      icon: icon('help'),
      ariaLabel: 'Help',
      ariaHaspopup: 'menu',
      // Declared closed rather than left unset: a `haspopup` control with no
      // `aria-expanded` is announced as a menu button whose state is unknown,
      // and `openPopoverMenu` only ever writes the attribute it finds.
      ariaExpanded: false,
      tooltip: 'Help, shortcuts and the tour',
      onClick: (e) => this._open(e.currentTarget)
    });
    this._container.appendChild(this._btn);
  }

  _open(triggerEl) {
    // Every dropdown in the app announces itself so the menu-hunt heuristic in
    // `ui/toolbar.js` still sees the bar's menus. It outlives this phase by one:
    // CMD-9 deletes it with the hint chip it feeds.
    eventBus.emit('toolbar:menu-opened');
    openPopoverMenu(triggerEl, {
      owner: 'command-bar-help',
      align: 'right',
      width: 232,
      menuLabel: 'Help',
      exitAnimation: MENU_EXIT_MS,
      items: [
        {
          id: 'command-palette',
          label: HELP_LABELS.find,
          icon: icon('search'),
          shortcut: 'Ctrl+K',
          onSelect: () => eventBus.emit('toolbar:open-command-palette')
        },
        {
          id: 'shortcuts',
          label: HELP_LABELS.shortcuts,
          icon: icon('shortcuts'),
          shortcut: '?',
          onSelect: () => eventBus.emit('toolbar:show-shortcuts')
        },
        {
          id: 'tour',
          label: HELP_LABELS.tour,
          icon: icon('tour'),
          onSelect: () => eventBus.emit('cmd:open-tour')
        },
        // IR-4. A report affordance lives where the defect is noticed, and the
        // standing rule this initiative exists because of is that a reporter
        // reachable from one obscure surface is indistinguishable from none.
        {
          id: 'report-issue',
          label: HELP_LABELS.report,
          icon: icon('flag'),
          onSelect: () => eventBus.emit('toolbar:report-issue')
        }
      ]
    });
  }
}
